import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { jsPDF } from 'jspdf';
import { UtilsService } from './utils.service';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class PdfGeneratorService {
  downloading = false;

  constructor(
    private utilsService: UtilsService,
    private http: HttpClient,
  ) {}

  getStatus(status: any) {
    switch (status) {
      case 'validating':
        return 'Pendiente';
      case 'rejected':
        return 'Rechazada';
      case 'approved':
        return 'Aprobada';
      default:
        return '';
    }
  }

  fetchImageAsBase64(url: string): Promise<string> {
    return this.getFileBase64FromUrl(url);
  }

  getFileBase64FromUrl(url: string): Promise<string> {
    return this.http
      .post<{ status: string; data: string }>(
        `${environment.fileApiBaseUrl}/file/base64`,
        { data_url: url },
      )
      .toPromise()
      .then((res) => res?.data || '');
  }

  async generateKycPdf(item: any) {
    if (this.downloading) return;
    this.downloading = true;

    const doc = new jsPDF();
    const MARGIN = 20;
    const MAX_Y = 280;
    const pageWidth = doc.internal.pageSize.getWidth();

    let y = MARGIN + 40; // espacio para logo y título

    const checkPageBreak = () => {
      if (y >= MAX_Y) {
        doc.addPage();
        y = MARGIN;
      }
    };
    const VALUE_INDENT = 5; // mm
    const LINE_HEIGHT = 8; // mm por línea
    const FIELD_SPACING = 4; // espacio inferior entre campos

    const addText = (label: string, value: string) => {
      const labelWrapWidth = pageWidth - 2 * MARGIN;
      const valueWrapWidth = pageWidth - 2 * MARGIN - VALUE_INDENT;

      // Label (bold)
      const wrappedLabel = doc.splitTextToSize(`${label}:`, labelWrapWidth);
      doc.setFont('helvetica', 'bold');
      doc.text(wrappedLabel, MARGIN, y);
      y += wrappedLabel.length * LINE_HEIGHT;

      // Value (normal, con indent)
      const wrappedValue = doc.splitTextToSize(value || '', valueWrapWidth);
      doc.setFont('helvetica', 'normal');
      wrappedValue.forEach((line: any, i: any) => {
        doc.text(line, MARGIN + VALUE_INDENT, y + i * LINE_HEIGHT);
      });
      y += wrappedValue.length * LINE_HEIGHT;

      // Espacio inferior
      y += FIELD_SPACING;
      checkPageBreak();
    };

    const addTitle = (text: string) => {
      const wrappedTitle = doc.splitTextToSize(text, pageWidth - 2 * MARGIN);

      // Si estamos muy cerca del texto anterior, agrega un poco de margen
      if (y > MARGIN) {
        y += 10; // margen superior solo si no es el comienzo
      }

      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');

      wrappedTitle.forEach((line: any) => {
        doc.text(line, pageWidth / 2, y, { align: 'center' });
        y += 10;
      });

      y += 5; // espacio inferior fijo
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      checkPageBreak();
    };

    const addLink = async (label: string, url: string) => {
      if (url) {
        const labelWrapWidth = pageWidth - 2 * MARGIN;
        const IMAGE_SIZE_MM = 42.3;
        const TEXT_OFFSET_BELOW_IMAGE = 8;

        const wrappedLabel = doc.splitTextToSize(`${label}:`, labelWrapWidth);
        doc.setFont('helvetica', 'bold');
        doc.text(wrappedLabel, MARGIN, y);
        y += wrappedLabel.length * LINE_HEIGHT;

        const isPdf = url.toLowerCase().endsWith('.pdf');

        if (isPdf) {
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(0, 0, 255);
          doc.textWithLink('Abrir documento PDF', MARGIN + VALUE_INDENT, y, {
            url,
          });
          doc.setTextColor(0, 0, 0);
          y += LINE_HEIGHT;
        } else {
          try {
            const base64 = await this.fetchImageAsBase64(url);

            doc.addImage(
              base64,
              'JPEG',
              MARGIN + VALUE_INDENT,
              y,
              IMAGE_SIZE_MM,
              IMAGE_SIZE_MM,
            );
            y += IMAGE_SIZE_MM + TEXT_OFFSET_BELOW_IMAGE;

            doc.setFont('helvetica', 'normal');
            doc.setTextColor(0, 0, 255);
            doc.textWithLink('Abrir imagen', MARGIN + VALUE_INDENT, y, { url });
            doc.setTextColor(0, 0, 0);
            y += LINE_HEIGHT;
          } catch (err) {
            doc.setFont('helvetica', 'italic');
            doc.setTextColor(255, 0, 0);
            doc.text('Error cargando imagen', MARGIN + VALUE_INDENT, y);
            doc.setTextColor(0, 0, 0);
            y += LINE_HEIGHT;
          }
        }
      }

      y += FIELD_SPACING;
      checkPageBreak();
    };

    // Logo
    const logoBase64 = await this.getBase64ImageFromURL(
      '/img/logo-green-black.png',
    );
    doc.addImage(logoBase64, 'PNG', MARGIN, MARGIN, 40, 0);

    // Título
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('KYC - Verificación personal', pageWidth / 2, MARGIN + 30, {
      align: 'center',
    });

    // Cuerpo
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');

    addText(
      'Fecha',
      `${this.utilsService.formatDate(
        item.datetime,
      )} - ${this.utilsService.formatHour(item.datetime)}`,
    );
    addText('Estatus', this.getStatus(item.status));

    if (this.getStatus(item.status) === 'Rechazada') {
      addText('Razón de rechazo', item.reason);
    }

    addText('País de verificación', item.country);

    doc.addPage();
    y = MARGIN;

    /* Detalles de la persona */
    addTitle('Detalles de la persona');

    addText('Primer nombre', item.first_name);

    if (item.middle_name) {
      addText('Segundo nombre', item.middle_name);
    }

    addText('Apellido', item.surname);
    addText('Número de documento', item.id_number);
    addText('País', item.issuance_country);
    addText('Nacionalidad', item.nationality);
    addText('Dirección', item.address);
    doc.addPage();
    y = MARGIN;
    addText('Cargo', item.job);
    addText('Email', item.email);
    addText('Número telefónico', item.phone_number);
    await addLink('Documento', item.document);
    addText('PEP', item.pep === '1' ? 'Sí' : 'No');
    if (item.pep === '1') {
      addText('Explicación de PEP', item.pep_explanation);
    }

    doc.addPage();
    y = MARGIN;

    /* Detalles de la persona */
    addTitle('Detalles financieros');

    addText('Ingreso anual', item.annual_income);
    await addLink('Documento de ingresos', item.income_proof);
    await addLink('Documento de residencia', item.address_proof);
    if (item.extra_file) {
      await addLink('Archivo extra (KYC externo)', item.extra_file);
    }

    doc.addPage();
    y = MARGIN;

    addText(
      'Declara taxes en US?',
      item.us_taxes_checkbox === '1' ? 'Sí' : 'No',
    );

    if (item.us_taxes_checkbox === '1') {
      addText('Explicación de taxes', item.tax_explanation);
    }

    addText('Tipo de negocio', item.business_type);
    addText('Transacciones estimadas', item.estimated_monthly_amount);
    addText('Monto estimado', item.estimated_monthly_transactions);

    doc.addPage();
    y = MARGIN;

    /* Cumplimiento */
    addTitle('Cumplimiento');

    addText('Origen de fondos', item.funds_origin);
    addText('Origen de recursos', item.resources_origin);
    addText('País de los fondos', item.funds_country);
    await addLink('Firma', item.signature);

    /* Campos adicionales */
    if (item.metadata) {
      doc.addPage();
      y = MARGIN;
      addTitle('Campos adicionales');
    }

    // Guardar
    doc.save('kyc_verification.pdf');
    this.downloading = false;
  }

  async generateKybPdf(item: any) {
    if (this.downloading) return;
    this.downloading = true;

    const doc = new jsPDF();
    const MARGIN = 20;
    const MAX_Y = 280;
    const pageWidth = doc.internal.pageSize.getWidth();
    const VALUE_INDENT = 5; // mm
    const LINE_HEIGHT = 8; // mm por línea
    const FIELD_SPACING = 4; // espacio inferior entre campos

    let y = MARGIN + 40; // espacio para logo y título

    const checkPageBreak = () => {
      if (y >= MAX_Y) {
        doc.addPage();
        y = MARGIN;
      }
    };

    const addText = (label: string, value: string) => {
      const labelWrapWidth = pageWidth - 2 * MARGIN;
      const valueWrapWidth = pageWidth - 2 * MARGIN - VALUE_INDENT;

      // Label (bold)
      const wrappedLabel = doc.splitTextToSize(`${label}:`, labelWrapWidth);
      doc.setFont('helvetica', 'bold');
      doc.text(wrappedLabel, MARGIN, y);
      y += wrappedLabel.length * LINE_HEIGHT;

      // Value (normal, con indent)
      const wrappedValue = doc.splitTextToSize(value || '', valueWrapWidth);
      doc.setFont('helvetica', 'normal');
      wrappedValue.forEach((line: any, i: any) => {
        doc.text(line, MARGIN + VALUE_INDENT, y + i * LINE_HEIGHT);
      });
      y += wrappedValue.length * LINE_HEIGHT;

      // Espacio inferior
      y += FIELD_SPACING;
      checkPageBreak();
    };

    const addLink = async (label: string, url: string) => {
      if (url) {
        const labelWrapWidth = pageWidth - 2 * MARGIN;
        const IMAGE_SIZE_MM = 42.3;
        const TEXT_OFFSET_BELOW_IMAGE = 8;

        const wrappedLabel = doc.splitTextToSize(`${label}:`, labelWrapWidth);
        doc.setFont('helvetica', 'bold');
        doc.text(wrappedLabel, MARGIN, y);
        y += wrappedLabel.length * LINE_HEIGHT;

        const isPdf = url.toLowerCase().endsWith('.pdf');

        if (isPdf) {
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(0, 0, 255);
          doc.textWithLink('Abrir documento PDF', MARGIN + VALUE_INDENT, y, {
            url,
          });
          doc.setTextColor(0, 0, 0);
          y += LINE_HEIGHT;
        } else {
          try {
            const base64 = await this.fetchImageAsBase64(url);
            doc.addImage(
              base64,
              'JPEG',
              MARGIN + VALUE_INDENT,
              y,
              IMAGE_SIZE_MM,
              IMAGE_SIZE_MM,
            );
            y += IMAGE_SIZE_MM + TEXT_OFFSET_BELOW_IMAGE;

            doc.setFont('helvetica', 'normal');
            doc.setTextColor(0, 0, 255);
            doc.textWithLink('Abrir imagen', MARGIN + VALUE_INDENT, y, { url });
            doc.setTextColor(0, 0, 0);
            y += LINE_HEIGHT;
          } catch (err) {
            doc.setFont('helvetica', 'italic');
            doc.setTextColor(255, 0, 0);
            doc.text('Error cargando imagen', MARGIN + VALUE_INDENT, y);
            doc.setTextColor(0, 0, 0);
            y += LINE_HEIGHT;
          }
        }
      }

      y += FIELD_SPACING;
      checkPageBreak();
    };

    const addTitle = (text: string) => {
      const wrappedTitle = doc.splitTextToSize(text, pageWidth - 2 * MARGIN);

      // Si estamos muy cerca del texto anterior, agrega un poco de margen
      if (y > MARGIN) {
        y += 10; // margen superior solo si no es el comienzo
      }

      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');

      wrappedTitle.forEach((line: any) => {
        doc.text(line, pageWidth / 2, y, { align: 'center' });
        y += 10;
      });

      y += 5; // espacio inferior fijo
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      checkPageBreak();
    };

    // Logo
    const logoBase64 = await this.getBase64ImageFromURL(
      '/img/logo-green-black.png',
    );
    doc.addImage(logoBase64, 'PNG', MARGIN, MARGIN, 40, 0);

    // Título
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('KYB - Verificación de empresas', pageWidth / 2, MARGIN + 30, {
      align: 'center',
    });

    // Cuerpo
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');

    addText(
      'Fecha',
      `${this.utilsService.formatDate(
        item.datetime,
      )} - ${this.utilsService.formatHour(item.datetime)}`,
    );
    addText('Estatus', this.getStatus(item.status));

    if (this.getStatus(item.status) === 'Rechazada') {
      addText('Razón de rechazo', item.reason);
    }

    addText('País de verificación ', item.country);

    doc.addPage();
    y = MARGIN;

    /* Detalles de la empresa */
    addTitle('Detalles de la empresa');
    addText('Nombre de la empresa', item.company_name);
    addText('Número de registro', item.company_registration_number);
    addText('Tipo de empresa', item.company_type);
    addText('Fecha de creación', item.company_established_date);
    addText('Ciudad de constitución', item.company_constitution_city_country);
    addText('Dirección de oficina', item.company_address_and_registered_office);
    addText('Dirección de notificación', item.company_notification_address);
    addText('Teléfono', item.company_phone_number);
    addText('Sitio web', item.company_website);
    addText('Correo electrónico', item.company_email);
    addText('Descripción', item.company_description);

    doc.addPage();
    y = MARGIN;

    /* Representante Legal */
    addTitle('Representante Legal');

    addText(
      'Nombre completo',
      `${item.legal_rep_first_name} ${item.legal_rep_middle_name} ${item.legal_rep_surname}`,
    );
    addText('# Documento', item.legal_rep_document);
    addText('País', item.legal_rep_issuance_country);
    addText('Dirección', item.legal_rep_address);

    if (item.legal_rep_document_file) {
      await addLink('Documento', item.legal_rep_document_file);
    }

    doc.addPage();
    y = MARGIN;

    /* Persona de contacto */
    addTitle('Persona de contacto');

    addText(
      'Nombre completo',
      `${item.contact_person_first_name} ${item.contact_person_middle_name} ${item.contact_person_surname}`,
    );
    addText('Cargo', item.contact_person_job_position);
    addText('Correo electrónico', item.contact_person_email);
    addText('Teléfono', item.contact_person_phone_number);
    addText('LinkedIn', item.contact_person_linkedin);

    doc.addPage();
    y = MARGIN;

    /* CEO */
    addTitle('CEO');

    addText(
      'Nombre completo',
      `${item.ceo_first_name} ${item.ceo_middle_name} ${item.ceo_surname}`,
    );
    addText('# Documento', item.ceo_document);
    addText('País', item.ceo_issuance_country);
    if (item.ceo_document_file) {
      await addLink('Documento', item.ceo_document_file);
    }

    addText('Filial', item.is_subsidiary === '1' ? 'Sí' : 'No');

    if (item.subsidiary_name) {
      addText('Nombre de la filial', item.subsidiary_name);
    }

    doc.addPage();
    y = MARGIN;

    /* Beneficiario #1 */
    addTitle('Beneficiario #1');

    addText(
      'Nombre completo',
      `${item.beneficiary_1_first_name} ${item.beneficiary_1_middle_name} ${item.beneficiary_1_surname}`,
    );
    addText('# Documento', item.beneficiary_1_document);
    addText('País', item.beneficiary_1_issuance_country);
    addText('% Participación', item.beneficiary_1_stake);
    if (item.beneficiary_1_document_file) {
      await addLink('Documento', item.beneficiary_1_document_file);
    }

    doc.addPage();
    y = MARGIN;

    /* Beneficiario #2 */
    addTitle('Beneficiario #2');

    addText(
      'Nombre completo',
      `${item.beneficiary_2_first_name} ${item.beneficiary_2_middle_name} ${item.beneficiary_2_surname}`,
    );
    addText('# Documento', item.beneficiary_2_document);
    addText('País', item.beneficiary_2_issuance_country);
    addText('% Participación', item.beneficiary_2_stake);
    if (item.beneficiary_2_document_file) {
      await addLink('Documento', item.beneficiary_2_document_file);
    }

    doc.addPage();
    y = MARGIN;

    /* Beneficiario #3 */
    if (item.beneficiary_3_first_name) {
      addTitle('Beneficiario #3');
      addText(
        'Nombre completo',
        `${item.beneficiary_3_first_name} ${item.beneficiary_3_middle_name} ${item.beneficiary_3_surname}`,
      );
      addText('# Documento', item.beneficiary_3_document);
      addText('País', item.beneficiary_3_issuance_country);
      addText('% Participación', item.beneficiary_3_stake);
      await addLink('Documento', item.beneficiary_3_document_file);

      doc.addPage();
      y = MARGIN;
    }

    /* Beneficiario #4 */
    if (item.beneficiary_4_first_name) {
      addTitle('Beneficiario #4');

      addText(
        'Nombre completo',
        `${item.beneficiary_4_first_name} ${item.beneficiary_4_middle_name} ${item.beneficiary_4_surname}`,
      );
      addText('# Documento', item.beneficiary_4_document);
      addText('País', item.beneficiary_4_issuance_country);
      addText('% Participación', item.beneficiary_4_stake);
      await addLink('Documento', item.beneficiary_4_document_file);

      doc.addPage();
      y = MARGIN;
    }

    /* CUMPLIMIENTO */
    addTitle('Cumplimiento');

    addText(
      '¿La empresa o su matriz o alguna de sus filiales ha sido objeto de una reclamación por negligencia en materia contractual, penal o de deuda?',
      item.negligence_checkbox === '1' ? 'Sí' : 'No',
    );

    addText(
      '¿La empresa, su empresa matriz o alguna de sus filiales han estado involucradas en procedimientos de quiebra o insolvencia, o se ha designado un síndico, administrador concursal o persona similar en los últimos 5 años?',
      item.bankrupcy_checkbox === '1' ? 'Sí' : 'No',
    );

    addText(
      '¿La empresa, su empresa matriz o alguna de sus filiales han tenido alguna vez sus licencias suspendidas o revocadas o han sido objeto de procedimientos disciplinarios en relación con dichas licencias?',
      item.suspended_checkbox === '1' ? 'Sí' : 'No',
    );

    addText(
      '¿La empresa o su empresa matriz o alguna de sus filiales ha sido objeto de una reclamación, sentencia, resolución o acuerdo extrajudicial en relación con una violación o supuesta violación de cualquier normativa de competencia o del mercado de valores en cualquier jurisdicción en los últimos cinco años?',
      item.competition_checkbox === '1' ? 'Sí' : 'No',
    );

    addText(
      '¿La empresa o su empresa matriz o alguna de sus subsidiarias ha sido procesada, demandada o condenada por algún delito o delito penal en cualquier jurisdicción en los últimos cinco años?',
      item.prosecuted_checkbox === '1' ? 'Sí' : 'No',
    );

    addText(
      'En particular, ¿la empresa, su empresa matriz o alguna de sus filiales ha sido sancionada por lavado de dinero o financiación del terrorismo?',
      item.sanctioned_checkbox === '1' ? 'Sí' : 'No',
    );

    addText(
      '¿Algún administrador, socio o gerente ha sido notificado de que usted está bajo investigación?',
      item.under_investigation_checkbox === '1' ? 'Sí' : 'No',
    );

    addText(
      '¿Alguno de los directivos o propietarios está involucrado o ha participado en actividades políticas (PEP) durante los últimos dos años?',
      item.pep_checkbox === '1' ? 'Sí' : 'No',
    );
    if (item.pep_explanation) {
      addText('Explicación', item.pep_explanation);
    }

    addText(
      '¿Está usted regulado por alguna entidad o autoridad externa?',
      item.regulated_checkbox === '1' ? 'Sí' : 'No',
    );
    if (item.regulated_explanation) {
      addText('Explicación', item.regulated_explanation);
    }

    addText(
      '¿Realiza usted auditorías internas o externas anualmente sobre cuestiones financieras y de AML?',
      item.aml_audits_checkbox === '1' ? 'Sí' : 'No',
    );
    if (item.aml_audtis_explanation) {
      addText('Explicación', item.aml_audtis_explanation);
    }

    doc.addPage();
    y = MARGIN;

    addTitle('Información Financiera');

    addText('Ventas anuales', item.annual_sales);
    addText('Activos totales', item.total_assets);
    addText('Ganancias netas', item.net_profit);
    addText('Cant. Empleados', item.employees_number);
    addText('Declara taxes', item.taxes_checkbox === '1' ? 'Sí' : 'No');
    if (item.tax_id) {
      addText('Tax ID', item.tax_id);
    }
    addText('Negocio esperado', item.business_type);
    addText('Transacciones estimadas', item.estimated_amount);
    addText('Monto mensual estimado', item.estimated_transactions);
    addText('Origen de los fondos', item.funds_origin);
    addText('Origen de los recursos', item.allocated_resources_origin);
    addText('País de los fondos', item.funds_country);

    /* DOCUMENTOS */
    doc.addPage();
    y = MARGIN;

    addTitle('Documentos');

    if (item.signature_file) {
      await addLink('Firma', item.signature_file);
    }

    if (item.constitution_documents) {
      await addLink('Estatutos', item.constitution_documents);
    }
    if (item.poa) {
      await addLink('POA', item.poa);
    }

    doc.addPage();
    y = MARGIN;

    if (item.alternative_license_documents) {
      await addLink('Licencia', item.alternative_license_documents);
    }

    if (item.aml_documents) {
      await addLink('Política ALD', item.aml_documents);
    }
    if (item.wolfsberg_group_CBDDQ_document) {
      await addLink(
        'Documento Wolfsberg Group CBDDQ',
        item.wolfsberg_group_CBDDQ_document,
      );
    }
    if (item.financial_statements) {
      await addLink('Estados Financieros', item.financial_statements);
    }

    doc.addPage();
    y = MARGIN;

    if (item.ownership_chart) {
      await addLink('Gráfica de propiedad', item.ownership_chart);
    }

    if (item.certificate_of_existence) {
      await addLink('Certificado de Existencia', item.certificate_of_existence);
    }
    if (item.pep_extra_information) {
      await addLink('Formularios PEP', item.pep_extra_information);
    }

    doc.addPage();
    y = MARGIN;

    if (item.bank_statements) {
      await addLink('Extracto de cuenta', item.bank_statements);
    }
    if (item.proof_of_address) {
      await addLink('Comprobante de domicilio', item.proof_of_address);
    }
    if (item.extra_file) {
      await addLink('Archivo extra (KYC externo)', item.extra_file);
    }

    if (item.metadata) {
      doc.addPage();
      y = MARGIN;
      addTitle('Campos adicionales');
    }

    // Guardar
    doc.save('kyb_verification.pdf');
    this.downloading = false;
  }

  private getBase64ImageFromURL(url: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.src = url;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject('No se pudo obtener el contexto del canvas');
          return;
        }
        ctx.drawImage(img, 0, 0);
        const dataURL = canvas.toDataURL('image/png');
        resolve(dataURL);
      };
      img.onerror = (error) => reject(error);
    });
  }
}
